import { lazyImport } from '@ambiki/impulse';

// The calendar pages (calendars/show's <therapist-calendar>, calendars/admin's #administrators_calendar) are the only
// application-layout pages that render the event form and its pickers: everything below loads only on them.
// events#new/#edit use the schedule pack, which registers its own copies. <patient-list> also renders on the
// teletherapy front desk, so its elements load wherever it appears (loadPatientListElements).
const CALENDAR_SELECTOR = 'therapist-calendar, #administrators_calendar';

// Memoized promise so the skd-* controllers are registered exactly once per
// hard page load. Calling register() again on every turbo:load
// force-disconnects and reconnects every skd-* controller on each visit.
let stimulusControllersLoaded = null;

/**
 * Asynchronously import and register the various stimulus controllers needed by the calendar system.
 * @async
 * @function loadStimulusControllers
 * @returns {Promise<void>}
 */
async function loadStimulusControllers() {
  // The application's own Stimulus instance (controllers/index.js), rather than a second one watching the document.
  const application = window.Stimulus;
  const SkdEventForm = (await import('../schedules/controllers/skd_event_form_controller')).default;
  application.register('skd-event-form', SkdEventForm);

  const SkdEventUpdateType = (await import('../schedules/controllers/skd_event_update_type_controller')).default;
  application.register('skd-event-update-type', SkdEventUpdateType);

  const SkdEventModeType = (await import('../schedules/controllers/skd_event_mode_type_controller')).default;
  application.register('skd-event-mode-type', SkdEventModeType);

  const SkdStaticMonthPicker = (await import('../schedules/controllers/skd_static_month_picker_controller')).default;
  application.register('skd-static-month-picker', SkdStaticMonthPicker);

  const SkdStaticWeekPicker = (await import('../schedules/controllers/skd_static_week_picker_controller')).default;
  application.register('skd-static-week-picker', SkdStaticWeekPicker);
}
// Load custom elements from the old schedule system. Every element that renders inside
// <patient-list> must be registered here: an Impulse element waits for all of its undefined
// descendants before it connects, so one missing registration leaves the whole header dead.
function loadPatientListElements() {
  return Promise.all([
    import('../schedules/elements/patient_attendance_element'),
    import('../schedules/elements/patient_bulk_confirm_element'),
    import('../schedules/elements/patient_checkin_element'),
    import('../schedules/elements/patient_confirm_element'),
    import('../schedules/elements/patient_list_element'),
  ]);
}
/**
 * Asynchronously loads the various modules used by the main calendar module.
 * @async
 * @function loadModulesCalendar
 * @returns {Promise<void>} - Resolves when the module is loaded.
 */
async function loadModulesCalendar() {
  await loadPatientListElements();
  // Load some newer stuff
  await import('./filters');
  await import('./undo');
}
/**
 * Asynchronously loads the administrators' calendar module.
 * @async
 * @function loadAdministratorsCalendar
 * @returns {Promise<void>} - Resolves when the module is loaded.
 */
async function loadAdministratorsCalendar() {
  await import('./administrators');
  await import('./calendar_header_element');
}
/**
 * Event listener for the 'turbo:load' event.
 * Loads the appropriate calendar module and Stimulus controllers.
 * @event turbo:load
 * @param {Event} event - The event object.
 */
document.addEventListener('turbo:load', async () => {
  // recurring-select, the checkbox elements and date-picker are registered with every other custom element by
  // custom-elements/index.js.
  if (!document.querySelector(CALENDAR_SELECTOR)) return;

  // Load Stimulus controllers first (registered once; later visits await the same promise)
  if (!stimulusControllersLoaded) {
    stimulusControllersLoaded = loadStimulusControllers();
  }
  await stimulusControllersLoaded;
  // Load all the other modules
  await loadModulesCalendar();

  // Check if the administrators' calendar element is present
  if (document.getElementById('administrators_calendar')) {
    await loadAdministratorsCalendar();
  }

  if (document.querySelector('therapist-calendar')) {
    await import('./therapists');
    await import('./calendar_header_element');
  }
});

lazyImport('event-location-select', () => import('./event_location_select_element'));
lazyImport('patient-list', loadPatientListElements);
